/**
 * PATH: packages/ui/src/components/find/findHighlightDom.ts
 * FILE_ID: UI-FIND-HIGHLIGHT-DOM-001
 * PURPOSE: Imperative, framework-free DOM helpers for the in-page
 *          ("mirror window") find: wrap every case-insensitive
 *          occurrence of a literal query inside a container's TEXT
 *          NODES with a <mark>, list the marks for next/prev cycling,
 *          mark the active one, and cleanly restore the DOM.
 * WHY:     The email body is rendered inline via dangerouslySetInnerHTML
 *          (EmailHtmlBody) — NOT an iframe — so a browser-Find-style
 *          highlight is feasible by walking text nodes. We never re-parse
 *          or re-serialise the HTML (preserves the sanitiser's XSS guard);
 *          we only split existing Text nodes and insert <mark> wrappers,
 *          and we fully reverse that on clear.
 * FALLBACK ONLY: splitting text nodes rewrites DOM that React owns, which
 *          makes React's next commit call removeChild() on a node we
 *          already replaced (NotFoundError → blank screen). Browsers with
 *          the CSS Custom Highlight API use findHighlightRanges.ts, which
 *          paints without touching the DOM. This file remains for the
 *          browsers that lack it. See findHighlightPainter.ts.
 * FLOW:
 *   applyHighlights(container, query) → split matching Text nodes →
 *     [<mark>…</mark>] (returned for the hook to cycle) ; clearHighlights
 *     unwraps every <mark data-qt-find-hit> back to plain text + normalises.
 * DEPENDENCIES:
 *   - ./findMatchScan: the shared needle/text-node/offset scanner, so the
 *     mark painter and the Range painter agree on what counts as a hit.
 * RELATED:
 *   - useInPageHighlight.ts (React orchestration: observe + active + scroll)
 *   - email/EmailHtmlBody.tsx (the inline body this runs over)
 */
import {
  collectMatchTextNodes,
  matchOffsets,
  normaliseNeedle,
} from './findMatchScan'

/** Marker attribute stamped on every hit so clear() can find + unwrap them. */
export const FIND_HIT_ATTR = 'data-qt-find-hit'
/** Marker attribute stamped on the single active hit. */
export const FIND_ACTIVE_ATTR = 'data-qt-find-active'

// Inline styles (not Tailwind classes) so dynamically-created marks are
// never purged and render identically in light/dark without a theme dep.
const HIT_STYLE =
  'background-color: rgba(253,224,71,0.75); color: inherit; border-radius: 2px;'
const ACTIVE_STYLE =
  'background-color: rgba(251,146,60,0.95); color: #1f2937; border-radius: 2px; box-shadow: 0 0 0 1px rgb(234,88,12);'

/**
 * Unwrap every find <mark> inside `container`, restoring the original
 * text, then coalesce the split text nodes. Idempotent + cheap when
 * there are no marks.
 */
export function clearHighlights(container: HTMLElement): void {
  const marks = container.querySelectorAll<HTMLElement>(`mark[${FIND_HIT_ATTR}]`)
  if (marks.length === 0) return
  marks.forEach((mark) => {
    mark.replaceWith(document.createTextNode(mark.textContent ?? ''))
  })
  // Merge the adjacent text nodes we just created back into single nodes
  // so a subsequent apply() walks clean, un-fragmented text.
  container.normalize()
}

/**
 * Highlight every occurrence of `rawQuery` (literal, case-insensitive,
 * trimmed) within `container`'s text nodes. Returns the created <mark>
 * elements in document order (for next/prev cycling). Clears any prior
 * highlights first. Empty/whitespace query → clears + returns [].
 */
export function applyHighlights(
  container: HTMLElement,
  rawQuery: string,
): HTMLElement[] {
  clearHighlights(container)
  const needle = normaliseNeedle(rawQuery)
  if (!needle) return []

  // Collect target text nodes first (don't mutate during traversal).
  const targets = collectMatchTextNodes(container, needle)

  const created: HTMLElement[] = []
  for (const textNode of targets) {
    highlightTextNode(textNode, needle, created)
  }
  return created
}

/** Split one Text node on every needle hit, inserting <mark> wrappers. */
function highlightTextNode(
  textNode: Text,
  needle: string,
  created: HTMLElement[],
): void {
  const value = textNode.nodeValue ?? ''
  const frag = document.createDocumentFragment()
  let cursor = 0
  for (const hit of matchOffsets(value, needle)) {
    if (hit > cursor) {
      frag.appendChild(document.createTextNode(value.slice(cursor, hit)))
    }
    const mark = document.createElement('mark')
    mark.setAttribute(FIND_HIT_ATTR, '')
    mark.setAttribute('style', HIT_STYLE)
    mark.textContent = value.slice(hit, hit + needle.length)
    frag.appendChild(mark)
    created.push(mark)
    cursor = hit + needle.length
  }
  if (cursor < value.length) {
    frag.appendChild(document.createTextNode(value.slice(cursor)))
  }
  textNode.replaceWith(frag)
}

/**
 * Style exactly one mark (by index) as the active hit and reset the
 * rest to the resting hit style. Returns the active element (for the
 * caller to scroll into view), or null when the index is out of range.
 */
export function setActiveHighlight(
  marks: HTMLElement[],
  activeIndex: number,
): HTMLElement | null {
  let active: HTMLElement | null = null
  marks.forEach((mark, i) => {
    if (i === activeIndex) {
      mark.setAttribute(FIND_ACTIVE_ATTR, '')
      mark.setAttribute('style', ACTIVE_STYLE)
      active = mark
    } else {
      mark.removeAttribute(FIND_ACTIVE_ATTR)
      mark.setAttribute('style', HIT_STYLE)
    }
  })
  return active
}
